// Barcode stickers printed straight from the browser, for a product, pallet
// or rack that's missing its label. The barcode drawn in the label pop-up is
// copied `count` times: on standard sticker sheets (64 × 25 mm stickers, 3
// across and 10 down on A4, filled a column at a time like the PDF), or one
// sticker per page on a thermal printer (its width and height in mm).

export const SHEET = { width: 64, height: 25, columns: 3, rows: 10, gap: 2.5 };
export const MAX_STICKERS = 300;

const escapeHtml = (text) =>
  String(text ?? "").replace(
    /[&<>"']/g,
    (ch) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
        ch
      ],
  );

// How many stickers to print: a whole number from 1 to MAX_STICKERS.
export const stickerCount = (value) => {
  const count = Math.floor(Number(value));
  if (!Number.isFinite(count) || count < 1) return 1;
  return Math.min(count, MAX_STICKERS);
};

const sticker = ({ barcodeSvg, name, image }) =>
  `<div class="sticker">${
    image ? `<img class="photo" src="${escapeHtml(image)}" alt="" />` : ""
  }<div class="code">${barcodeSvg}</div>${
    name ? `<div class="name">${escapeHtml(name)}</div>` : ""
  }</div>`;

/**
 * The print page. `layout` is { type: "standard" } or
 * { type: "thermal", width, height } (mm). `barcodeSvg` is the barcode's SVG
 * markup; `name` prints under it and `image` (a data URL) beside it, on
 * thermal stickers tall enough for it.
 */
export const stickersHtml = ({
  barcodeSvg,
  name = "",
  image = "",
  count = 1,
  layout = { type: "standard" },
  title = "Barcode stickers",
}) => {
  const total = stickerCount(count);
  const thermal = layout?.type === "thermal";
  const width = thermal ? Math.max(Number(layout.width) || 50, 10) : SHEET.width;
  const height = thermal
    ? Math.max(Number(layout.height) || 25, 10)
    : SHEET.height;
  const one = sticker({
    barcodeSvg,
    name,
    image: thermal && height >= 0.55 * width ? image : "",
  });
  const perPage = thermal ? 1 : SHEET.columns * SHEET.rows;
  const pages = [];
  for (let done = 0; done < total; done += perPage) {
    const onPage = Math.min(perPage, total - done);
    pages.push(`<div class="page">${one.repeat(onPage)}</div>`);
  }
  const pageCss = thermal
    ? `@page { size: ${width}mm ${height}mm; margin: 0; }
  .page { width: ${width}mm; height: ${height}mm; }`
    : `@page { size: A4 portrait; margin: 10mm; }
  .page { display: grid; grid-auto-flow: column;
    grid-template-columns: repeat(${SHEET.columns}, ${SHEET.width}mm);
    grid-template-rows: repeat(${SHEET.rows}, ${SHEET.height}mm);
    row-gap: ${SHEET.gap}mm; }`;
  return `<!doctype html>
<html><head><meta charset="utf-8" /><title>${escapeHtml(title)}</title>
<style>
  ${pageCss}
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: #fff; color: #000;
    font-family: Arial, Helvetica, sans-serif; }
  .page { break-after: page; page-break-after: always; overflow: hidden; }
  .page:last-child { break-after: auto; page-break-after: auto; }
  .sticker { width: ${width}mm; height: ${height}mm; padding: 1.5mm;
    display: flex; flex-direction: column; align-items: center;
    justify-content: center; overflow: hidden; }
  .photo { max-height: 40%; max-width: 60%; object-fit: contain;
    margin-bottom: 1mm; }
  .code { flex: 1 1 auto; min-height: 0; width: 100%; display: flex;
    align-items: center; justify-content: center; }
  .code svg { max-width: 100%; max-height: 100%; width: auto; height: auto; }
  .name { font-size: 7pt; font-weight: 600; line-height: 1.15;
    max-width: 100%; white-space: nowrap; overflow: hidden;
    text-overflow: ellipsis; }
</style></head>
<body>${pages.join("")}</body></html>`;
};

/**
 * Opens the print page in a small window and prints it; the window closes
 * once printing is done. Returns false when the browser blocked the window.
 */
export const printStickers = (options, openWindow = window.open) => {
  const win = openWindow("", "_blank", "width=480,height=640");
  if (!win) return false;
  win.document.open();
  win.document.write(stickersHtml(options));
  win.document.close();
  // Prints once: when the page has loaded, or shortly after if it already has.
  let printed = false;
  const print = () => {
    if (printed || win.closed) return;
    printed = true;
    win.focus();
    win.print();
  };
  win.onafterprint = () => win.close();
  win.onload = print;
  setTimeout(print, 300);
  return true;
};
